// FLAG:useDocuAi — expense/revenue upload via the DMS→DocuAI pipeline (replaces the
// legacy inline-XML POST /api/file/upload path). Gated at the call sites by
// isDmsReceiptUploadEnabled() (useDocuAi && dmsDirectUpload); flag-OFF keeps the legacy route.
// Files are routed into the Finance system folder so the DMS analysis result surfaces as a
// "Zu prüfen" review row in the expense/revenue list; the actual booking is created later
// from the reviewed data via createExpenseFromReviewedInvoice.
import { RECEIPT_TYPES, SystemFolders } from '@/modules/base/util/constants'
import { TAG_LABELS } from '@/modules/file/constants'
import FileService from '@/modules/file/fileService'
import { executeUpload } from '@/redux/ducks/document/uploadHelpers'

export const RECEIPT_DMS_UPLOAD_ACTIONS = {
  CREATE_EXPENSE: 'createExpense',
  CREATE_REVENUE: 'createRevenue',
  UPLOAD_FOR_RECEIPT: 'uploadForReceipt',
} as const

export type ReceiptDmsUploadAction = typeof RECEIPT_DMS_UPLOAD_ACTIONS[keyof typeof RECEIPT_DMS_UPLOAD_ACTIONS]

export const RECEIPT_DMS_ALLOWED_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'xml', 'zugferd']
export const RECEIPT_DMS_TRIGGER_ALLOWED_EXTENSIONS = RECEIPT_DMS_ALLOWED_EXTENSIONS.map(extension => `.${extension}`)

const getReceiptFolderPath = (type: string) => {
  return type === RECEIPT_TYPES.EXPENSE
    ? SystemFolders.FinanceExpense
    : SystemFolders.FinanceRevenue
}

export const getReceiptCreateUploadAction = (type: string): ReceiptDmsUploadAction => {
  return type === RECEIPT_TYPES.EXPENSE
    ? RECEIPT_DMS_UPLOAD_ACTIONS.CREATE_EXPENSE
    : RECEIPT_DMS_UPLOAD_ACTIONS.CREATE_REVENUE
}

export const buildReceiptDmsUploadParams = (
  { type, action, receiptId }: { type: string, action: ReceiptDmsUploadAction, receiptId?: number | string | null },
) => ({
  folderId: null,
  // FLAG:useDocuAi — the incoming-invoice tag ('Rechnungseingang') makes prepare-upload derive the
  // DMS documentType 'incomingInvoices' (prepare-upload.js), which is required for the analysis
  // upload (extractKeyValue=true, set by scan). Without it DMS rejects the token: "extractKeyValue
  // is only allowed for incomingInvoices type uploads". Mirrors the Documents upload (tags:[kind.label]).
  // NOTE: applied for expense AND revenue — a revenue receipt is thereby tagged as incoming invoice;
  // acceptable for now (enables DocuAI analysis; only INCOMING_INVOICE maps to an analyzable DMS type),
  // revisit when the revenue review path is built out.
  tags: [TAG_LABELS.INCOMING_INVOICE],
  ...(action === RECEIPT_DMS_UPLOAD_ACTIONS.UPLOAD_FOR_RECEIPT ? {} : { externalId: `receiptUpload:${action}` }),
  metaData: {
    path: getReceiptFolderPath(type),
    receiptUploadAction: action,
    // FLAG:useDocuAi — T25: WHICH receipt this upload belongs to. Only 'uploadForReceipt' carries
    // it (the create intents ARE the record), and only when the form has a saved receipt. The
    // server links the file to it when the upload completes, so an abandoned form no longer
    // leaves a document behind that belongs to nobody.
    // `receiptTarget` travels WITH the id: the stored metaData loses `path` (the upload completion
    // and the analysis rewrite the column), so the server cannot read the ledger back off the
    // folder path — measured 2026-09-18 on files 24196–24198, which carried intent and receiptId
    // but no path and were therefore never linked. Authorisation still happens on the PATH in
    // prepare-upload, before any of this is stored; this is only the ledger name.
    ...(action === RECEIPT_DMS_UPLOAD_ACTIONS.UPLOAD_FOR_RECEIPT && receiptId
      ? { receiptId: Number(receiptId), receiptTarget: type === RECEIPT_TYPES.REVENUE ? 'revenue' : 'expense' }
      : {}),
  },
})

// FLAG:useDocuAi — how many files go into one executeUpload batch. A batch is the unit that
// settles, so an unbounded batch (the expense dropzone had no cap at all) means the tile cannot
// report anything until the very last file is done and one wedged file holds the whole drop
// hostage. 25 matches the two caps that already exist in this codebase — receipt-edit.tsx's
// MAX_FILES_PER_UPLOAD and createDmsUploadHandler's limit.
export const RECEIPT_UPLOAD_BATCH_SIZE = 25

export type ReceiptUploadProgress = { done: number, total: number }

// F15.3 — a failed entry keeps its uuid AND its original File reference so the retry can be
// identity-true; a name join misfires on same-named files (Scanner-Output 'scan.pdf' twice).
export type ReceiptFailedUploadEntry = { uuid?: string, name: string, file?: File }

export type ReceiptUploadResult = {
  data: Array<{ id: string, name: string }>,
  completedWithErrors: number,
  errors: unknown[],
  failedFiles: ReceiptFailedUploadEntry[],
}

export const uploadReceiptFilesToDms = async({
  files,
  type,
  action = getReceiptCreateUploadAction(type),
  receiptId,
  scan = true,
  onFileUploaded,
  onProgress,
}: {
  files: File[],
  type: string,
  action?: ReceiptDmsUploadAction,
  receiptId?: number | string | null,
  scan?: boolean,
  onFileUploaded?: (fileId: string) => void,
  onProgress?: (progress: { done: number, total: number, failed: number }) => void,
}) => {
  return executeUpload({
    files,
    scan,
    // Route by receipt type so upload placeholders surface in the matching list
    // (expenses for expense receipts, revenues for revenue receipts) rather than the document inbox.
    source: type === RECEIPT_TYPES.EXPENSE ? 'expense' : 'revenue',
    params: buildReceiptDmsUploadParams({ type, action, receiptId }),
    onFileUploaded,
    onProgress,
  })
}

/**
 * FLAG:useDocuAi — upload an arbitrarily large drop as a sequence of bounded batches.
 *
 * Sequential on purpose: the batches share one concurrency pool, so running them in parallel would
 * just re-create the unbounded burst this is meant to prevent. Each batch settles before the next
 * starts, which is what makes the tile advance and keeps one bad batch from poisoning the rest.
 *
 * Failures ACCUMULATE across batches — a later batch must not overwrite an earlier batch's
 * failures, or the tile would under-report.
 */
export const uploadReceiptFilesToDmsInBatches = async({
  files,
  type,
  action = getReceiptCreateUploadAction(type),
  receiptId,
  scan = true,
  batchSize = RECEIPT_UPLOAD_BATCH_SIZE,
  onProgress,
}: {
  files: File[],
  type: string,
  action?: ReceiptDmsUploadAction,
  receiptId?: number | string | null,
  scan?: boolean,
  batchSize?: number,
  onProgress?: (progress: ReceiptUploadProgress) => void,
}): Promise<ReceiptUploadResult> => {
  const aggregated: ReceiptUploadResult = { data: [], completedWithErrors: 0, errors: [], failedFiles: [] }
  let settled = 0

  for (let start = 0; start < files.length; start += batchSize) {
    const batch = files.slice(start, start + batchSize)
    try {
      const result = await uploadReceiptFilesToDms({
        files: batch,
        type,
        action,
        receiptId,
        scan,
        // Offset by the files already settled so the count the user sees is monotonic across
        // batches instead of restarting at 0 for each one.
        onProgress: ({ done }) => onProgress?.({ done: settled + done, total: files.length }),
      }) as Partial<ReceiptUploadResult> & { failedUploads?: ReceiptFailedUploadEntry[] }

      aggregated.data.push(...(result?.data ?? []))
      aggregated.completedWithErrors += result?.completedWithErrors ?? 0
      aggregated.errors.push(...(result?.errors ?? []))
      aggregated.failedFiles.push(...(result?.failedUploads ?? []))
    } catch (error) {
      // The batch rejected outright (the stall backstop). F15.2: the sub-batch's already-settled
      // results survive the reject — successes stay in the aggregate (the tile retry must not
      // re-upload them), settled failures keep their reasons, and only the files that actually
      // made no progress count as stalled failures on top.
      const stallError = error as Error & {
        stalledFiles?: string[],
        stalledUploads?: ReceiptFailedUploadEntry[],
        partial?: Partial<ReceiptUploadResult> & { failedUploads?: ReceiptFailedUploadEntry[] },
      }
      const partial = stallError?.partial
      aggregated.data.push(...(partial?.data ?? []))
      aggregated.errors.push(...(partial?.errors ?? []))
      aggregated.failedFiles.push(...(partial?.failedUploads ?? []))
      const stalledCount = stallError?.stalledFiles?.length
        ?? Math.max(batch.length - (partial?.data?.length ?? 0) - (partial?.completedWithErrors ?? 0), 0)
      aggregated.completedWithErrors += (partial?.completedWithErrors ?? 0) + stalledCount
      aggregated.failedFiles.push(...(stallError?.stalledUploads ?? []))
      // Deliberately NOT pushing error.message: the raw English 'Upload stalled: …' would become
      // the failure tile's text (getReceiptUploadFailureMessage takes the first string). Without
      // a string here the tile falls back to the German count summary; per-file reasons are in
      // the flyout.
    }

    settled += batch.length
    onProgress?.({ done: settled, total: files.length })
  }

  return aggregated
}

export const uploadReceiptFilesToDmsAndFetchInfo = async(options: {
  files: File[],
  type: string,
  action?: ReceiptDmsUploadAction,
  scan?: boolean,
}) => {
  const uploadResult = await uploadReceiptFilesToDms(options) as { data?: Array<{ id: string, name: string }>, completedWithErrors?: number, errors?: unknown[] }
  const uploadedFiles = await Promise.all((uploadResult.data ?? []).map(async(file) => {
    try {
      const { body: fileInfo } = await FileService.fetchFileInfo(file.id)
      return {
        ...fileInfo,
        fileId: fileInfo.id,
      }
    } catch {
      return {
        ...file,
        fileId: file.id,
      }
    }
  }))

  return {
    ...uploadResult,
    files: uploadedFiles,
  }
}
